import { useState } from 'react'
import { Code2, Copy, Check, Download } from 'lucide-react'

export type XmlVariant = {
  key: string
  label: string
  fetcher: () => Promise<string>
  filename: string
}

type Props = {
  variants: XmlVariant[]
  title?: string
}

export function formatXml(xml: string): string {
  try {
    let formatted = ''
    let indent = 0
    const parts = xml.replace(/>\s*</g, '><').split(/(<[^>]+>)/g).filter(Boolean)
    for (const part of parts) {
      if (part.startsWith('</')) {
        indent = Math.max(0, indent - 1)
        formatted += '  '.repeat(indent) + part + '\n'
      } else if (part.startsWith('<') && part.endsWith('/>')) {
        formatted += '  '.repeat(indent) + part + '\n'
      } else if (part.startsWith('<?')) {
        formatted += part + '\n'
      } else if (part.startsWith('<') && !part.startsWith('</')) {
        formatted += '  '.repeat(indent) + part + '\n'
        indent++
      } else {
        formatted = formatted.trimEnd() + part + '\n'
      }
    }
    return formatted.trimEnd()
  } catch {
    return xml
  }
}

function triggerBlobDownload(content: string, filename: string) {
  const blob = new Blob([content], { type: 'application/xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function XmlViewer({ variants, title = 'XML del Documento' }: Props) {
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  if (!variants.length) return null

  const activeVariant = variants.find(v => v.key === activeKey) || null

  const handleSelect = async (variant: XmlVariant) => {
    if (activeKey === variant.key) {
      setActiveKey(null)
      setContent('')
      setError(null)
      return
    }
    setActiveKey(variant.key)
    setLoading(true)
    setError(null)
    setContent('')
    try {
      const xml = await variant.fetcher()
      setContent(xml)
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      setError(err.response?.data?.error?.message ?? err.message ?? 'No se pudo obtener el XML.')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!content) return
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = () => {
    if (!content || !activeVariant) return
    triggerBlobDownload(content, activeVariant.filename)
  }

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-content-muted mb-2">{title}</p>
      <div className="flex items-center gap-2 flex-wrap">
        {variants.map(v => (
          <button
            key={v.key}
            onClick={() => handleSelect(v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              activeKey === v.key
                ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300'
                : 'bg-surface border-divider text-content-muted hover:bg-soft'
            }`}
          >
            <Code2 size={13} />
            {v.label}
          </button>
        ))}
      </div>

      {activeVariant && (
        <div className="mt-3 rounded-xl border border-divider overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-surface-muted border-b border-divider">
            <span className="text-xs font-medium text-content-muted">
              {activeVariant.label}
              {content && (
                <span className="ml-2 text-content-subtle">
                  ({Math.round(content.length / 1024)} KB)
                </span>
              )}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={handleCopy}
                disabled={!content}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted hover:text-content hover:bg-surface-muted rounded transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                title="Copiar XML"
              >
                {copied ? <Check size={12} className="text-success-solid" /> : <Copy size={12} />}
                {copied ? 'Copiado' : 'Copiar'}
              </button>
              <button
                onClick={handleDownload}
                disabled={!content}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted hover:text-content hover:bg-surface-muted rounded transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                title="Descargar XML"
              >
                <Download size={12} /> Descargar
              </button>
            </div>
          </div>
          <div className="bg-surface-muted">
            {loading && (
              <p className="p-3 text-xs text-content-muted">Cargando XML…</p>
            )}
            {!loading && error && (
              <p className="p-3 text-xs text-danger-content-soft">{error}</p>
            )}
            {!loading && !error && content && (
              <pre className="p-3 text-[11px] leading-relaxed font-mono text-content-muted overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre">
                {formatXml(content)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export async function fetchXmlAsText(url: string, apiClientGet: (u: string, opts?: unknown) => Promise<{ data: Blob | string }>): Promise<string> {
  const res = await apiClientGet(url, { responseType: 'blob' })
  if (typeof res.data === 'string') return res.data
  return await (res.data as Blob).text()
}
